import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import '../McmvForm.css';
import './Dashboard.css';
import { openRegistrationReceipt } from '../../utils/printReceipt';
import McmvRegistrationForm from '../McmvCad';

interface PreRegistration {
  id: number;
  numero_inscricao?: string;
  nome_completo: string;
  cpf: string;
  email?: string;
  status: string; // exemplos: 'inscricao_recebida', 'contemplada', 'nao_contemplada'
  created_at: string;
  updated_at: string;
}

const ApoioDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [activeSection, setActiveSection] = useState('lista');
  const [registrations, setRegistrations] = useState<PreRegistration[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [editingRegistration, setEditingRegistration] = useState<PreRegistration | null>(null);
  // Apoio: lista somente leitura

  // Debounce da busca para reduzir carga no backend
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    if (activeSection === 'lista') {
      fetchRegistrations();
    }
  }, [activeSection, currentPage, debouncedSearch, statusFilter, perPage]);

  const fetchRegistrations = async () => {
    setLoading(true);
    try {
      const response = await api.get('/mcmv/pre-registrations', {
        params: {
          page: currentPage,
          per_page: perPage,
          search: debouncedSearch || undefined,
          status: statusFilter || undefined,
        }
      });
      const payload = response.data;
      // Aceita payloads paginados { data: [], last_page } ou lista direta []
      if (payload && Array.isArray(payload.data)) {
        setRegistrations(payload.data);
        setTotalPages(payload.last_page || 1);
      } else if (Array.isArray(payload)) {
        setRegistrations(payload);
        setTotalPages(1);
      } else if (payload?.data?.data) {
        setRegistrations(payload.data.data);
        setTotalPages(payload.data.last_page || 1);
      } else {
        setRegistrations([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Erro ao buscar inscrições:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'inscricao_recebida':
        return 'Inscrição recebida';
      case 'contemplada':
        return 'Contemplada';
      case 'nao_contemplada':
        return 'Não contemplada';
      case 'em_analise':
        return 'Em análise';
      default:
        return status;
    }
  };

  const renderRegistrationsList = () => (
    <div className="registrations-section">
      <div className="section-header">
        <h2>Gerenciar Inscrições</h2>
        <div className="filters">
          <input
            type="text"
            placeholder="Buscar por nome, CPF ou email..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            className="search-input"
          />
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            className="status-filter"
          >
            <option value="">Todos os status</option>
            <option value="inscricao_recebida">Inscrição recebida</option>
            <option value="em_analise">Em análise</option>
            <option value="contemplada">Contemplada</option>
            <option value="nao_contemplada">Não contemplada</option>
          </select>
          <select
            value={perPage}
            onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}
            className="status-filter"
            style={{ marginLeft: 8 }}
          >
            <option value={10}>10 por página</option>
            <option value={20}>20 por página</option>
            <option value={50}>50 por página</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">Carregando...</div>
      ) : (
        <>
          <div className="registrations-table">
            <table>
              <thead>
                <tr>
                  <th>Nº Inscrição</th>
                  <th>Nome</th>
                  <th>CPF</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Data</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {registrations.map((registration) => (
                  <tr key={registration.id}>
                    <td>{registration.numero_inscricao ?? '-'}</td>
                    <td>{registration.nome_completo}</td>
                    <td>{registration.cpf}</td>
                    <td>{registration.email ?? '-'}</td>
                    <td>
                      <span className={`status-badge status-${registration.status}`}>
                        {getStatusLabel(registration.status)}
                      </span>
                    </td>
                    <td>{new Date(registration.created_at).toLocaleDateString()}</td>
                    <td>
                      <button
                        onClick={() => {
                          setEditingRegistration(registration);
                          setActiveSection('editar');
                        }}
                        className="btn btn-sm"
                        title="Editar inscrição"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => openRegistrationReceipt({
                          numero_inscricao: registration.numero_inscricao || 'N/A',
                          nome: registration.nome_completo,
                          cpf: registration.cpf,
                          email: registration.email,
                          created_at: registration.created_at,
                          status: registration.status,
                        })}
                        className="btn btn-sm"
                        style={{ marginLeft: 8 }}
                        title="Imprimir comprovante"
                      >
                        Imprimir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
              >
                {page}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'lista':
        return renderRegistrationsList();
      case 'nova':
        return (
          <div className="dashboard-section">
            <h2>Nova Inscrição</h2>
            <p>Preencha os dados abaixo para registrar uma nova inscrição no programa.</p>
            <div style={{ marginTop: 16 }}>
              <McmvRegistrationForm 
                disablePrefill={true}
                disableLoadOwnRegistration={true}
              />
            </div>
          </div>
        );
      case 'editar':
        return (
          <div className="dashboard-section">
            <h2>Editar Inscrição</h2>
            {editingRegistration && (
              <p>Editando: {editingRegistration.nome_completo} (ID {editingRegistration.id})</p>
            )}
            <div style={{ marginTop: 16 }}>
              {editingRegistration && (
                <McmvRegistrationForm
                  editId={editingRegistration.id}
                  disablePrefill={true}
                  disableLoadOwnRegistration={true}
                  onSuccess={() => {
                    setEditingRegistration(null);
                    setActiveSection('lista');
                    fetchRegistrations();
                  }}
                />
              )}
            </div>
          </div>
        );
      default:
        return renderRegistrationsList();
    }
  };

  return (
    <div className="apoio-dashboard">
      <div className="dashboard-header">
        <h1>Painel de Apoio MCMV</h1>
        <div className="user-info">
          <span>Bem-vindo(a), {user?.name}</span>
          <button onClick={logout} className="btn btn-secondary">
            Sair
          </button>
        </div>
      </div>

      <div className="dashboard-nav">
        <button
          onClick={() => setActiveSection('lista')}
          className={`nav-btn ${activeSection === 'lista' ? 'active' : ''}`}
        >
          📋 Listar Inscrições
        </button>
        <button
          onClick={() => setActiveSection('nova')}
          className={`nav-btn ${activeSection === 'nova' ? 'active' : ''}`}
          style={{ marginLeft: 8 }}
        >
          🆕 Nova Inscrição
        </button>
      </div>

      <div className="dashboard-content">
        {renderContent()}
      </div>
    </div>
  );
};

export default ApoioDashboard;